import React, { useMemo, useState } from 'react'
import { usePage } from '@inertiajs/inertia-react'
import Layout from '../../Shared/Layout/LayoutAdmin'
import LayoutSiaLearner from '../../Shared/Layout/LayoutSiaLearner'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import { SIA_GOLD_ACTIVE_BG, SIA_GOLD_ACTIVE_TEXT, SIA_NAVY } from '../../constants/siaTheme'

function parseAmount(raw) {
    const cleaned = String(raw || '').replace(/[^\d.]/g, '')
    if (!cleaned || cleaned === '.') {
        return null
    }
    const value = Number(cleaned)
    if (!Number.isFinite(value) || value <= 0) {
        return null
    }
    return value
}

function formatMoney(value) {
    const hasCents = Math.round(value * 100) % 100 !== 0
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
    }).format(value)
}

const IdealSum = ({ idealYears = 10, suggestedYears = 5 }) => {
    const { sia_use_learner_layout: learnerLayout } = usePage().props
    const [raw, setRaw] = useState('')
    const [submittedEmpty, setSubmittedEmpty] = useState(false)

    const monthly = useMemo(() => parseAmount(raw), [raw])
    const annual = monthly == null ? null : monthly * 12
    const ideal = annual == null ? null : annual * idealYears
    const suggested = annual == null ? null : annual * suggestedYears

    const onSubmit = (event) => {
        event.preventDefault()
        setSubmittedEmpty(monthly == null)
    }

    return (
        <>
            {!learnerLayout && (
                <div className="mb-5 mt-5">
                    <Breadcrumbs currentPath="/admin/sia/suma-asegurada-ideal" />
                </div>
            )}

            <div className={'d-flex justify-content-center px-2 ' + (learnerLayout ? 'pb-10 pt-2' : 'pb-10')}>
                <div
                    className="w-100 rounded-3 shadow-sm bg-white overflow-hidden"
                    style={{ maxWidth: '880px', border: '1px solid #e8e8e8' }}
                >
                    <div className="text-center text-white py-5 px-4" style={{ backgroundColor: SIA_NAVY }}>
                        <div className="text-uppercase fw-bold fs-3" style={{ letterSpacing: '0.06em' }}>
                            Suma asegurada ideal
                        </div>
                        <div className="mt-2 opacity-75">
                            A partir del ingreso mensual del profesor
                        </div>
                    </div>

                    <div className="py-8 px-6 px-md-10">
                        <form onSubmit={onSubmit}>
                            <label htmlFor="monthly-income" className="form-label fw-semibold text-gray-800">
                                Ingreso mensual del profesor
                            </label>
                            <div className="d-flex flex-column flex-sm-row gap-3">
                                <div className="position-relative flex-grow-1">
                                    <span
                                        className="position-absolute top-50 translate-middle-y text-gray-500 fw-semibold"
                                        style={{ left: '14px' }}
                                    >
                                        $
                                    </span>
                                    <input
                                        id="monthly-income"
                                        inputMode="decimal"
                                        className="form-control form-control-lg ps-8"
                                        placeholder="20000"
                                        value={raw}
                                        onChange={(event) => {
                                            setRaw(event.target.value)
                                            setSubmittedEmpty(false)
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-lg fw-semibold px-8"
                                    style={{
                                        backgroundColor: SIA_GOLD_ACTIVE_BG,
                                        color: SIA_GOLD_ACTIVE_TEXT,
                                        border: 'none',
                                    }}
                                >
                                    Calcular
                                </button>
                            </div>
                            {submittedEmpty && (
                                <div className="text-danger fs-7 mt-2">Escribe un ingreso mensual mayor a cero.</div>
                            )}
                        </form>

                        {monthly == null ? (
                            <p className="text-gray-600 mt-8 mb-0">
                                El ingreso anual es el mensual por 12. La suma ideal usa {idealYears} años de ese ingreso
                                y la sugerida usa {suggestedYears}.
                            </p>
                        ) : (
                            <>
                                <div className="row g-4 mt-6">
                                    <div className="col-md-6">
                                        <div className="rounded-3 p-5 h-100" style={{ backgroundColor: '#f4f7fb' }}>
                                            <div className="text-gray-600 fs-7 text-uppercase fw-semibold">Ingreso mensual</div>
                                            <div className="fs-2 fw-bold text-gray-900 mt-1">{formatMoney(monthly)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="rounded-3 p-5 h-100" style={{ backgroundColor: '#f4f7fb' }}>
                                            <div className="text-gray-600 fs-7 text-uppercase fw-semibold">Ingreso anual</div>
                                            <div className="fs-2 fw-bold text-gray-900 mt-1">{formatMoney(annual)}</div>
                                            <div className="text-gray-600 fs-7 mt-1">Mensual × 12</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4 mt-1">
                                    <div className="col-md-6">
                                        <div
                                            className="rounded-3 p-6 h-100"
                                            style={{ backgroundColor: '#fbf6e8', border: '1px solid #e6d48a' }}
                                        >
                                            <div className="fw-semibold" style={{ color: '#8a7014' }}>
                                                Suma ideal
                                            </div>
                                            <div className="text-gray-700 fs-7 mt-1">{idealYears} años de trabajo</div>
                                            <div className="fs-1 fw-bold mt-3" style={{ color: SIA_NAVY }}>
                                                {formatMoney(ideal)}
                                            </div>
                                            <div className="text-gray-600 fs-7 mt-2">Ingreso anual × {idealYears}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div
                                            className="rounded-3 p-6 h-100"
                                            style={{ backgroundColor: '#f7f8fa', border: '1px solid #e4e6ef' }}
                                        >
                                            <div className="fw-semibold text-gray-800">Suma sugerida</div>
                                            <div className="text-gray-700 fs-7 mt-1">{suggestedYears} años de trabajo</div>
                                            <div className="fs-1 fw-bold text-gray-900 mt-3">{formatMoney(suggested)}</div>
                                            <div className="text-gray-600 fs-7 mt-2">Ingreso anual × {suggestedYears}</div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

IdealSum.layout = (page) => {
    if (page.props.sia_use_learner_layout) {
        return <LayoutSiaLearner title="Suma asegurada ideal | YIREH">{page}</LayoutSiaLearner>
    }
    return <Layout title="Suma asegurada ideal">{page}</Layout>
}

export default IdealSum
